import { z } from 'zod';
import { nullableArray, nullableOptional } from '@shigo-ui/api-client';

import { networkConfigurableRoles } from '../networks/networkRoles';

export enum AdminUsersContext {
  Main = 'MAIN',
  SuperAdmin = 'SUPER_ADMIN',
}

export enum AdminUserStatus {
  Active = 'ACTIVE',
  Archived = 'ARCHIVED',
}

export enum AdminUserSortProperty {
  Name = 'name',
  Username = 'username',
}

export enum AdminUserSortDirection {
  Asc = 'ASC',
  Desc = 'DESC',
}

export function isAdminUserSortProperty(value: string): value is AdminUserSortProperty {
  return Object.values(AdminUserSortProperty).includes(value as AdminUserSortProperty);
}

export const adminUserFilterRoles = networkConfigurableRoles;
const adminUserFilterRoleValues = new Set<string>(adminUserFilterRoles);

export function isAdminUserFilterRole(
  value: string,
): value is (typeof adminUserFilterRoles)[number] {
  return adminUserFilterRoleValues.has(value);
}

export const adminUserPublisherSchema = z.looseObject({
  id: z.string(),
  name: z.string(),
});

export const adminUserNetworkSchema = z.looseObject({
  id: z.union([z.string(), z.number()]),
  name: z.string(),
  status: nullableOptional(z.string()),
});

export const adminUserMembershipSchema = z.looseObject({
  membershipId: nullableOptional(z.string()),
  userId: nullableOptional(z.string()),
  userEmail: nullableOptional(z.string()),
  networkId: z.union([z.string(), z.number()]),
  networkName: z.string(),
  enabled: nullableOptional(z.boolean()),
  roles: nullableArray(z.string()),
  roleIds: nullableArray(z.number().int()),
  rights: nullableArray(z.string()),
  publisherIds: nullableArray(z.string()),
  agencyIds: nullableArray(z.string()).optional(),
  contentOwnerIds: nullableArray(z.string()),
  advertiserId: z.string().nullable().optional(),
});

export const adminUserSchema = z.looseObject({
  id: nullableOptional(z.string()),
  username: nullableOptional(z.string()),
  email: z.string(),
  name: z.string(),
  roles: nullableArray(z.string()),
  publisherIds: nullableArray(z.string()),
  agencyIds: nullableArray(z.string()).optional(),
  publishers: nullableArray(adminUserPublisherSchema),
  enabled: nullableOptional(z.boolean()),
  network: nullableOptional(adminUserNetworkSchema),
  memberships: nullableArray(adminUserMembershipSchema),
  createdOn: nullableOptional(z.string()),
  lastUpdatedOn: nullableOptional(z.string()),
});

export const adminUsersPageSchema = z.looseObject({
  content: z.array(adminUserSchema),
  totalElements: z.number().int().nonnegative(),
  number: z.number().int().nonnegative().optional(),
  size: z.number().int().positive().optional(),
  totalPages: z.number().int().nonnegative().optional(),
});

export const adminUserMutationResponseSchema = z.preprocess(
  (value) => (value === '' || value === undefined ? null : value),
  adminUserSchema.nullable(),
);

export type AdminUser = z.infer<typeof adminUserSchema>;
export type AdminUserNetwork = z.infer<typeof adminUserNetworkSchema>;
export type AdminUserMembership = z.infer<typeof adminUserMembershipSchema>;
export type AdminUserPublisher = z.infer<typeof adminUserPublisherSchema>;
export type AdminUsersPage = z.infer<typeof adminUsersPageSchema>;

export interface AdminUsersFilter {
  searchTerm: string;
  statuses: AdminUserStatus[];
  role: string;
  page: number;
  size: number;
  sortProperty: AdminUserSortProperty | null;
  sortDirection: AdminUserSortDirection;
}

export interface AdminUserMutationPayload {
  username?: string | undefined;
  email: string;
  name: string;
  password?: string | undefined;
  enabled?: boolean | undefined;
  roles?: string[] | undefined;
  publisherIds?: string[] | undefined;
  agencyIds?: string[] | undefined;
  memberships?: AdminUserMembershipMutationPayload[] | undefined;
}

export interface AdminUserMembershipMutationPayload {
  networkId: number;
  enabled: boolean;
  roles: string[];
  roleIds: number[];
  rights: string[];
  publisherIds: string[];
  agencyIds: string[];
  contentOwnerIds: string[];
  advertiserId: string | null;
}

export const adminUsersPageSizes = [5, 10, 25, 50, 100] as const;

export const defaultAdminUsersFilter: AdminUsersFilter = {
  searchTerm: '',
  statuses: [AdminUserStatus.Active],
  role: '',
  page: 0,
  size: 25,
  sortProperty: AdminUserSortProperty.Name,
  sortDirection: AdminUserSortDirection.Asc,
};

export function statusForAdminUser(user: AdminUser) {
  return user.enabled === false ? AdminUserStatus.Archived : AdminUserStatus.Active;
}

export function normalizeRegularAdminUsersPage(
  users: AdminUser[],
  filter: AdminUsersFilter,
): AdminUsersPage {
  const direction = filter.sortDirection === AdminUserSortDirection.Asc ? 1 : -1;
  const sorted = [...users].sort((left, right) => {
    if (!filter.sortProperty) return 0;
    const leftValue = valueForSort(left, filter.sortProperty);
    const rightValue = valueForSort(right, filter.sortProperty);
    return leftValue.localeCompare(rightValue, undefined, { sensitivity: 'base' }) * direction;
  });
  const offset = filter.page * filter.size;
  return {
    content: sorted.slice(offset, offset + filter.size),
    totalElements: sorted.length,
    number: filter.page,
    size: filter.size,
    totalPages: Math.ceil(sorted.length / filter.size),
  };
}

function valueForSort(user: AdminUser, property: AdminUserSortProperty) {
  return property === AdminUserSortProperty.Username ? (user.username ?? user.email) : user.name;
}
